import { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ShieldCheck, Lock, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";

const PRESETS = [25, 50, 100, 250, 500, 1000];

export default function Donate() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [frequency, setFrequency] = useState("one_time");
  const [method, setMethod] = useState("paypal");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("canceled")) toast.info("Donation canceled. You can try again anytime.");
  }, []);

  const effectiveAmount = custom ? parseFloat(custom) : amount;

  const donate = async () => {
    const amt = custom ? parseFloat(custom) : amount;
    if (!amt || amt < 5) {
      toast.error("Minimum donation is $5.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/donations/checkout", {
        amount: amt,
        donor_name: name,
        donor_email: email,
        origin_url: window.location.origin,
        frequency,
        provider: method,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout");
      setLoading(false);
    }
  };

  return (
    <div>
      <Navbar />
      <section className="pt-28 sm:pt-36 pb-24 bg-[#0A1128] text-white grain stars tactical-grid min-h-screen relative overflow-hidden">
        <div className="absolute top-1/4 -left-40 w-[520px] h-[520px] glow-red pointer-events-none" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 relative z-10">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <span className="kicker-pill mb-5">Fund The Mission</span>
              <h1 className="font-display text-5xl sm:text-6xl uppercase mt-3 leading-[0.92]">
                Fuel The <span className="text-[#D90429]">Fight</span>
              </h1>
              <p className="text-white/70 mt-6 leading-relaxed">
                Every dollar trains a patriot, equips a chapter, and strengthens our
                nation's front line of citizen defense. Your contribution goes directly
                to preparedness programs and community response.
              </p>
              <ul className="mt-8 space-y-4">
                {["$25 supplies an emergency kit", "$100 funds a training session", "$500 equips a local response team"].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-white/80">
                    <ShieldCheck className="w-5 h-5 text-[#D90429] mt-0.5 shrink-0" /> {t}
                  </li>
                ))}
              </ul>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-3 bg-white text-[#0A1128] p-6 sm:p-10"
            >
              <h2 className="font-display text-2xl uppercase mb-4">Select Amount</h2>
              <div className="grid grid-cols-3 gap-3">
                {PRESETS.map((p) => (
                  <button
                    key={p}
                    data-testid={`preset-${p}`}
                    onClick={() => { setAmount(p); setCustom(""); }}
                    className={`py-4 font-display text-2xl border-2 transition-colors duration-200 ${
                      !custom && amount === p
                        ? "border-[#D90429] bg-[#D90429] text-white"
                        : "border-slate-200 hover:border-[#D90429]"
                    }`}
                  >
                    ${p}
                  </button>
                ))}
              </div>

              <div className="mt-4">
                <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Custom Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg">$</span>
                  <input
                    data-testid="custom-amount-input"
                    type="number"
                    min="5"
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    placeholder="Other amount"
                    className="w-full border border-slate-300 pl-8 pr-4 py-3 rounded-none focus:outline-none focus:border-[#D90429] focus:ring-1 focus:ring-[#D90429] transition-colors duration-200"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mt-6">
                <input
                  data-testid="donor-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Name (optional)"
                  className="border border-slate-300 px-4 py-3 rounded-none focus:outline-none focus:border-[#D90429] transition-colors duration-200"
                />
                <input
                  data-testid="donor-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional)"
                  className="border border-slate-300 px-4 py-3 rounded-none focus:outline-none focus:border-[#D90429] transition-colors duration-200"
                />
              </div>

              <div className="mt-6">
                <label className="block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">Payment Method</label>
                <div className="grid grid-cols-2 gap-3" data-testid="payment-method-toggle">
                  {[["paypal", "PayPal"], ["stripe", "Credit / Debit Card"]].map(([val, label]) => (
                    <button
                      key={val}
                      type="button"
                      data-testid={`pay-method-${val}`}
                      onClick={() => setMethod(val)}
                      className={`py-3 uppercase tracking-[0.12em] text-xs font-bold border-2 transition-colors duration-200 ${
                        method === val
                          ? "border-[#0A1128] bg-[#0A1128] text-white"
                          : "border-slate-200 text-slate-500 hover:border-[#0A1128]"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={donate}
                disabled={loading}
                data-testid="donate-submit-button"
                className="w-full mt-8 bg-[#D90429] text-white py-4 uppercase tracking-[0.2em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-[0.99] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : `Donate $${effectiveAmount || 0}`}
              </button>
              <p className="flex items-center justify-center gap-2 text-xs text-slate-400 mt-4">
                <Lock className="w-3.5 h-3.5" /> Secure checkout — PayPal or card
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
